import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Box, Button, Chip, Tooltip, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { Activity, BookOpen, Building2, ClipboardList, Edit, FileText, Network, KeyRound, Receipt, Quote as QuoteIcon, Users } from "lucide-react"

import { useCan } from "@/auth/permissions"
import { BackButton } from "@/components/ui/back-button"
import { PageLoader } from "@/components/ui/page-loader"
import { useToast } from "@/components/ui/use-toast"
import { DetailTabs, type DetailTab } from "@crm-src/components/detail-tabs"
import { useFinancialAccess } from "@crm-src/components/money"
import { PagePermissionGuard } from "@crm-src/components/page-permission-guard"
import { PurchaseOrdersTab } from "@crm-src/components/purchase-orders/purchase-orders-tab"
import usePermissions from "@crm-src/hooks/usePermissions"
import { useTabParam } from "@crm-src/hooks/useTabParam"
import { resellersApiService } from "@crm-src/lib/resellers-api"
import { resellerIsActive, type Reseller } from "@crm-src/types/lead"

import { ResellerActivationControl } from "./ResellerActivationControl"
import { ResellerAccountsTab } from "./ResellerAccountsTab"
import { ResellerActivityTab } from "./ResellerActivityTab"
import { ResellerEditDialog } from "./ResellerEditDialog"
import { ResellerFilesTab } from "./ResellerFilesTab"
import { ResellerKnowledgeBaseTab } from "./ResellerKnowledgeBaseTab"
import { ResellerLicencesTab } from "./ResellerLicencesTab"
import { ResellerMoneyTab } from "./ResellerMoneyTab"
import { ResellerNetworkTab } from "./ResellerNetworkTab"
import { ResellerOverviewTab } from "./ResellerOverviewTab"

/**
 * The reseller profile.
 *
 * Nine tabs (ten for a documentation curator), through the SAME
 * `<DetailTabs>` + `useTabParam` pair the customer
 * and prospect pages use — so `?tab=files` deep-links, Back returns to the list
 * rather than walking every tab the operator clicked, and only the visible
 * tab's data is fetched. It replaces a single ~780-line scroll of stacked cards
 * that fetched everything on mount whether or not anyone looked at it.
 *
 * THE FINANCIAL WALL decides the tab SET, not just the tab contents: without
 * `crm:financials` the Quotes and Invoices tabs are filtered out and their
 * panels never mount, and `useTabParam` falls a `?tab=invoices` deep-link back
 * to Overview rather than rendering an empty money page. That is the same
 * treatment the customer page gives its Billing tab. The **Documentation**
 * tab is filtered the same way on `documentation:manage_access` — a
 * dashboard permission, not a CRM one, because what it edits lives in
 * portal-api (2026-09-14).
 */
const ALL_TABS: DetailTab[] = [
  { id: "overview", label: "Overview", icon: Building2 },
  { id: "network", label: "Network", icon: Network },
  { id: "files", label: "Files", icon: FileText },
  { id: "activity", label: "Activity", icon: Activity },
  // The PEOPLE inside the company (2026-09-12, moved from System Admin ▸
  // Reseller Accounts): invite, role, permissions, revoke.
  { id: "accounts", label: "Accounts", icon: Users },
  // The money tabs. Filtered out below without `crm:financials`. Purchase
  // Orders (2026-09-17) are what the reseller sends BACK to XOP.
  { id: "quotes", label: "Quotes", icon: QuoteIcon },
  { id: "invoices", label: "Invoices", icon: Receipt },
  { id: "purchase-orders", label: "Purchase Orders", icon: ClipboardList },
  // Licences for this reseller's customers and any it created (2026-09-15).
  // Filtered out below without the CRM `licences:access` page grant.
  { id: "licences", label: "Licences", icon: KeyRound },
  // What this company may read in the documentation — the Access editor
  // with the account pre-picked. Filtered out without `documentation:manage_access`.
  { id: "documentation", label: "Documentation", icon: BookOpen },
]

const MONEY_TABS = new Set(["quotes", "invoices", "purchase-orders"])
const DOCUMENTATION_TAB = "documentation"
// `?tab=knowledge-base` was this tab's id from 2026-09-14 to 2026-09-17; old
// links still open it (and the URL is rewritten to `?tab=documentation`).
const TAB_ALIASES = { "knowledge-base": DOCUMENTATION_TAB }
const LICENCES_TAB = "licences"

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

function ResellerDetailsPage() {
  const params = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const resellerId = params.id as string

  const [reseller, setReseller] = useState<Reseller | null>(null)
  const [loading, setLoading] = useState(true)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)

  const hasFinancialAccess = useFinancialAccess()
  const canManageKnowledgeBaseAccess = useCan("documentation:manage_access")
  const { canManageResellers, canAccessPage } = usePermissions()
  const canSeeLicences = canAccessPage("licences")

  const visibleTabs = ALL_TABS.filter(
    (tab) =>
      (hasFinancialAccess || !MONEY_TABS.has(tab.id)) &&
      (canManageKnowledgeBaseAccess || tab.id !== DOCUMENTATION_TAB) &&
      (canSeeLicences || tab.id !== LICENCES_TAB),
  )
  const [activeTab, setActiveTab] = useTabParam(
    visibleTabs.map((tab) => tab.id),
    "overview",
    "tab",
    TAB_ALIASES,
  )

  useEffect(() => {
    let cancelled = false
    setLoading(true)

    resellersApiService
      .getById(resellerId)
      .then((data) => {
        if (!cancelled) setReseller(data)
      })
      .catch(() => {
        if (cancelled) return
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load reseller details",
        })
        navigate("/crm/administration/resellers")
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // `toast` and `navigate` are stable; re-running on them would refetch the
    // reseller on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resellerId])

  const reload = async (): Promise<void> => {
    const data = await resellersApiService.getById(resellerId)
    setReseller(data)
  }

  if (loading) return <PageLoader />
  if (!reseller) return null

  const active = resellerIsActive(reseller)

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Tooltip describeChild title="Return to resellers list">
          <BackButton
            label="Back to Resellers"
            onClick={() => navigate("/crm/administration/resellers")}
            sx={{ mb: 2 }}
          />
        </Tooltip>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "primary.main",
              }}
            >
              <Building2 size={24} />
            </Box>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography component="h1" sx={{ fontSize: "1.5rem", fontWeight: 700 }}>
                  {reseller.name}
                </Typography>
                {reseller.code && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "secondary.main",
                      bgcolor: (t) => alpha(t.palette.secondary.main, 0.1),
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                    }}
                  >
                    {reseller.code}
                  </Box>
                )}
                {/* The account state is in the HEADER, not buried in a tab: it
                    changes what every other tab means. */}
                <Chip
                  data-cy="reseller-activation-chip"
                  size="small"
                  label={active ? "Active" : "Deactivated"}
                  color={active ? "success" : "error"}
                  variant={active ? "outlined" : "filled"}
                />
              </Box>
              <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 0.5 }}>
                Created {formatDate(reseller.created_at)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ResellerActivationControl reseller={reseller} onChanged={setReseller} />
            {canManageResellers() && (
              <Tooltip describeChild title="Edit reseller information and address">
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  startIcon={<Edit size={16} />}
                  onClick={() => setIsEditDialogOpen(true)}
                  data-cy="reseller-edit"
                >
                  Edit Reseller
                </Button>
              </Tooltip>
            )}
          </Box>
        </Box>

        {!active && (
          <Box
            data-cy="reseller-deactivated-banner"
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 1,
              bgcolor: (t) => alpha(t.palette.error.main, 0.08),
              border: 1,
              borderColor: (t) => alpha(t.palette.error.main, 0.25),
            }}
          >
            <Typography sx={{ fontSize: 14, color: "error.dark", fontWeight: 500 }}>
              This account is deactivated — nobody from {reseller.name} can sign in to the reseller portal.
            </Typography>
            {reseller.activation?.reason && (
              <Typography sx={{ fontSize: 13, color: "error.dark", mt: 0.5 }}>
                Reason: {reseller.activation.reason}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      <DetailTabs
        tabs={visibleTabs}
        activeId={activeTab}
        onTabChange={setActiveTab}
        ariaLabel="Reseller sections"
      />

      <Box sx={{ mt: 3 }}>
        {activeTab === "overview" && <ResellerOverviewTab reseller={reseller} onChanged={reload} />}
        {activeTab === "network" && <ResellerNetworkTab resellerId={resellerId} />}
        {activeTab === "files" && <ResellerFilesTab resellerId={resellerId} />}
        {activeTab === "activity" && <ResellerActivityTab resellerId={resellerId} />}
        {activeTab === "accounts" && <ResellerAccountsTab resellerId={resellerId} resellerName={reseller.name} />}
        {/* `hasFinancialAccess` is re-checked here as well as in `visibleTabs`:
            the tab is already unreachable without it, and a money panel is not
            something to leave guarded in only one place. */}
        {activeTab === "quotes" && hasFinancialAccess && (
          <ResellerMoneyTab resellerId={resellerId} kind="quote" />
        )}
        {activeTab === "invoices" && hasFinancialAccess && (
          <ResellerMoneyTab resellerId={resellerId} kind="invoice" />
        )}
        {activeTab === "purchase-orders" && hasFinancialAccess && <PurchaseOrdersTab resellerId={resellerId} />}
        {activeTab === LICENCES_TAB && canSeeLicences && <ResellerLicencesTab resellerId={resellerId} />}
        {activeTab === DOCUMENTATION_TAB && canManageKnowledgeBaseAccess && (
          <ResellerKnowledgeBaseTab resellerId={resellerId} resellerName={reseller.name} />
        )}
      </Box>

      <ResellerEditDialog
        open={isEditDialogOpen}
        reseller={reseller}
        onClose={() => setIsEditDialogOpen(false)}
        onSaved={async () => {
          setIsEditDialogOpen(false)
          await reload()
        }}
      />
    </Box>
  )
}

export default function GuardedResellerDetailsPage() {
  return (
    <PagePermissionGuard requiredPermission="resellers:access" redirectTo="/crm">
      <ResellerDetailsPage />
    </PagePermissionGuard>
  )
}
